import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { Guid } from "@/Common/Models/Guid";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { Site } from "./ResponseModels/Site";

class SitesModel {
    public Model: Site;

    constructor(model: Site) {
        this.Model = model;
    }

    public getFormattedPhoneByType(phoneType: PhoneType): string {
        if (this.Model?.Phones?.any() !== true)
            return "";

        return new PhoneNumber(this.Model.Phones.firstOrDefault(p => {
            let typeLookupId = cacheStore.getLookupByPhoneType(phoneType)?.LookupID;
            return p.PhoneTypeLookupId == typeLookupId;
        })).format("({1}) {2} {3}");
    }

    public getFormattedAddress(): string {
        let address = this.Model?.Adresses?.firstPreferredOrDefault(p => p.IsPreferred);
        if (!address)
            return "";

        return Address.GetFullAddress(address);
    }

    public isSiteInProvince(province: CanadaState): boolean {
        const provinceLookupId = cacheStore.GetLookupIdForProvince(province);

        return this.Model?.Adresses?.some(p => p.StateLookupId === provinceLookupId);
    }

    public isPreferredSiteAddressInProvince(province: CanadaState): boolean {
        const provinceLookupId = cacheStore.GetLookupIdForProvince(province);
        const preferredAddressLookup = Address.getDefaultAddressStateLookup(this.Model.Adresses);

        return preferredAddressLookup?.LookupID === provinceLookupId;
    }

    public static fromSiteResponse(siteResponse: Site): SitesModel {
        return new SitesModel(siteResponse);
    }

    public static ToComboboxItem(site: SitesModel): ComboBoxDropDownItem {
        return new ComboBoxDropDownItem(site.Model.Id, site.Model.ClinicalName, 0);
    }

    public isCanadaSite(): boolean {
        return Address.isDefaultAddressInCanada(this.Model.Adresses);
    }

    public isMexicoSite(): boolean {
        return this.getCountryLookupId() === cacheStore.FindLookupByLookupCode("CNMEXICO").LookupID;
    }

    public isColombiaSite(): boolean {
        return this.getCountryLookupId() === cacheStore.FindLookupByLookupCode("CNCOLOMBIA").LookupID;
    }

    public getCountryLookupId(): string {
        return this.Model?.Adresses?.[0]?.CountryLookupId;
    }

    public getStateLookupId(): string {
        return this.Model?.Adresses?.[0]?.StateLookupId;
    }

    public isHst(): boolean {
        const provinceLookup = Address.getDefaultAddressStateLookup(this.Model?.Adresses);
        return EnvironmentTax.isHstProvinceByLookupCode(provinceLookup?.LookupCode);
    }

    public isGst(): boolean {
        const provinceLookup = Address.getDefaultAddressStateLookup(this.Model?.Adresses);
        return EnvironmentTax.isGstProvinceByLookupCode(provinceLookup?.LookupCode);
    }

    public isPstOrQst(): boolean {
        const provinceLookup = Address.getDefaultAddressStateLookup(this.Model?.Adresses);
        return EnvironmentTax.isPstOrQstProvinceByLookupCode(provinceLookup?.LookupCode);
    }

    public getPstOrQstLabel(): string {
        if (this.isPreferredSiteAddressInProvince(CanadaState.Quebec))
            return cacheStore.GetString("QstMLabel");

        return cacheStore.GetString("PstMLabel");
    }

    public getPreferredAddress(): Address {
        return Address.getDefaultAddress(this.Model?.Adresses);
    }

    public getAddressLabel(): string {
        if (this.Model.Adresses && this.Model.Adresses.length > 0) {
            const defaultAddress: Address = this.Model.Adresses[0];
            const defaultAddressState: string = cacheStore.GetLocalizedValueByLookupId(defaultAddress.StateLookupId);
            const defaultAddressCountry: string = cacheStore.GetLocalizedValueByLookupId(defaultAddress.CountryLookupId);

            return `${defaultAddress.StreetAddress}, ${defaultAddress.City}, ${defaultAddressState}, ${defaultAddressCountry}, ${defaultAddress.ZipCode}`;
        }
        return this.Model.AddressDsq;
    }

    public getPhoneLabel(): string {
        if (this.Model.Phones?.length) {
            const faxLookup = cacheStore.FindLookupByLookupCode("FAX", "PHONETYPE");
            const phoneLookup = cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE");

            const fax = this.Model.Phones.filter(p => p.PhoneTypeLookupId == faxLookup.LookupID)[0];
            const phone = this.Model.Phones.filter(p => p.PhoneTypeLookupId == phoneLookup.LookupID).firstPreferredOrDefault(p => p.IsPreferred);

            return (
                (fax ? `FAX: ${PhoneNumber.getLabelFormat(fax.Number)} ` : "")
                + (phone ? `TEL: ${PhoneNumber.getLabelFormat(phone.Number)}` : "")
                + (phone && phone.Extension ? ` ext: ${phone.Extension}` : "")
            );
        }

        return this.Model.PhoneDsq;
    }

    public getId(): Guid {
        return this.Model.Id;
    }
}

export { SitesModel };
